import { isNull, isUndefined } from "@videojs/utils/predicate";
import { findLastAtOrBefore } from "@videojs/utils/array";
//#region src/core/ui/thumbnail/core.ts
var ThumbnailCore = class {
	findActiveThumbnail(thumbnails, time) {
		return findLastAtOrBefore(thumbnails, time, (thumbnail) => thumbnail.startTime);
	}
	/**
	* Parse CSS constraint strings into numeric `ThumbnailConstraints`.
	*
	* Accepts any object with string `minWidth`/`maxWidth`/`minHeight`/`maxHeight` properties — `CSSStyleDeclaration`
	* satisfies this structurally.
	*/
	parseConstraints(raw) {
		const minW = parseFloat(raw.minWidth);
		const maxW = parseFloat(raw.maxWidth);
		const minH = parseFloat(raw.minHeight);
		const maxH = parseFloat(raw.maxHeight);
		return {
			minWidth: Number.isFinite(minW) ? minW : 0,
			maxWidth: Number.isFinite(maxW) ? maxW : Infinity,
			minHeight: Number.isFinite(minH) ? minH : 0,
			maxHeight: Number.isFinite(maxH) ? maxH : Infinity
		};
	}
	/**
	* Calculate a uniform scale factor that sizes `tileWidth × tileHeight` to the given CSS min/max constraints while
	* preserving aspect ratio.
	*
	* - Fills the max constraints, scaling the tile up as readily as down. A box that grows — entering fullscreen widens it
	*   through a container query — has to take the tile with it rather than leave it at its native size.
	* - Raises that to meet min constraints, which win over max as they do in CSS.
	* - Returns `1` when unconstrained.
	*/
	calculateScale(tileWidth, tileHeight, constraints) {
		const { minWidth, maxWidth, minHeight, maxHeight } = constraints;
		const maxRatio = Math.min(maxWidth / tileWidth, maxHeight / tileHeight);
		const minRatio = Math.max(minWidth / tileWidth, minHeight / tileHeight);
		const scale = Number.isFinite(maxRatio) ? maxRatio : 1;
		return Number.isFinite(minRatio) && minRatio > scale ? minRatio : scale;
	}
	/**
	* Compute container and image dimensions for the current thumbnail, scaled to the element's CSS min/max constraints.
	*
	* The container clips the sprite sheet via `overflow: hidden`, and the image is positioned with `transform:
	* translate()` to show the correct tile.
	*/
	resize(thumbnail, imgNaturalWidth, imgNaturalHeight, constraints) {
		const tileWidth = thumbnail.width ?? imgNaturalWidth;
		const tileHeight = thumbnail.height ?? imgNaturalHeight;
		if (!tileWidth || !tileHeight) return void 0;
		const scale = this.calculateScale(tileWidth, tileHeight, constraints);
		const coordX = thumbnail.coords?.x ?? 0;
		const coordY = thumbnail.coords?.y ?? 0;
		const inset = scale !== 1 ? 1 : 0;
		return {
			scale,
			containerWidth: Math.max(0, Math.floor(tileWidth * scale) - inset * 2),
			containerHeight: Math.max(0, Math.floor(tileHeight * scale) - inset * 2),
			imageWidth: Math.ceil(imgNaturalWidth * scale),
			imageHeight: Math.ceil(imgNaturalHeight * scale),
			offsetX: Math.ceil(coordX * scale) + inset,
			offsetY: Math.ceil(coordY * scale) + inset
		};
	}
	/**
	* Resolve the CORS mode the image should request with.
	*
	* `null` opts out and drops the attribute. Any other explicit value wins, including `''`, which the CORS-settings
	* attribute reads as Anonymous. Otherwise the inherited mode applies, which renderers supply only for
	* `<track>`-sourced thumbnails since a list set directly may point at a host unrelated to the media element.
	*/
	resolveCrossOrigin(explicit, inherited) {
		if (isNull(explicit)) return void 0;
		if (!isUndefined(explicit)) return explicit;
		return inherited ?? void 0;
	}
	getState(loading, error, thumbnail) {
		return {
			loading,
			error,
			hidden: !loading && !thumbnail
		};
	}
	getAttrs(_state) {
		return {
			dir: "ltr",
			role: "img",
			"aria-hidden": "true"
		};
	}
};
//#endregion
export { ThumbnailCore };

//# sourceMappingURL=core.js.map